// React
import { useEffect, useRef, useState, type ChangeEvent, type Dispatch, type SetStateAction } from 'react';
import { useSelector } from 'react-redux';

import { Paperclip, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

// FutureLab
import { ChatRichTextEditor } from './ChatRichTextEditor';
import { connectSocket, useSocket } from '../../hooks/useSocket';
import { LoggedInUserDetails, RootState } from '../../reducers';
import { useCreateAttachmentUploadsMutation } from '../../store/api/chatApi';

import { trackMixpanelEvent } from '../../services/mixpanel';
import { UserDetailsListItem } from '../../model';
import { store } from '../../ReduxRoot';
import { isSameUserId, toUserId } from '../../utilities/UserId';
import {
	MAX_CHAT_ATTACHMENTS,
	MAX_CHAT_ATTACHMENT_SIZE,
	isAllowedChatAttachment,
	releaseAttachmentPreview,
	uploadAttachmentsToS3,
	type QueuedAttachment,
} from './attachment';

interface SendChatForm {
	chatMessage?: string;
}

interface SendChatMessageProps {
	updateSeenMessages: () => void;
	files: QueuedAttachment[];
	setFiles: Dispatch<SetStateAction<QueuedAttachment[]>>;
	loggedInUserDetails: LoggedInUserDetails;
}

export function SendChatMessage(props: SendChatMessageProps) {
	const { updateSeenMessages, files, setFiles, loggedInUserDetails } = props;
	const socket = useSocket();
	const [createAttachmentUploads] = useCreateAttachmentUploadsMutation();
	const chatInFocus = useSelector((state: RootState) => state.chatInFocus);
	const [formValues, setFormValues] = useState<SendChatForm>({
		chatMessage: '',
	});
	const [isUploading, setIsUploading] = useState(false);
	const filesRef = useRef(files);

	useEffect(() => {
		filesRef.current = files;
	}, [files]);

	useEffect(
		() => () => {
			filesRef.current.forEach(releaseAttachmentPreview);
		},
		[]
	);

	const chatSenderUserId = chatInFocus.participants.find(
		(participant) => isSameUserId(participant.userId, toUserId(loggedInUserDetails.id))
	);

	const chatSender: UserDetailsListItem | undefined = store
		.getState()
		.userListProfilesDetails.find(
			(participant: UserDetailsListItem) => participant?.id === chatSenderUserId?.userId
		);

	const chatSenderFullName = `${chatSender?.firstname} ${chatSender?.lastname ?? ''}`;

	const formValue = (data: SendChatForm) => {
		setFormValues(data);
	};

	const handleSubmit = async () => {
		const message = formValues.chatMessage?.trim() ?? '';
		if (isUploading || (!message && files.length === 0)) {
			return;
		}
		if (!connectSocket(socket)) {
			toast.error('Your chat session has expired. Please sign in again.');
			return;
		}

		trackMixpanelEvent('Chat Message Send', {
			'Message Send': message,
			Attachment: files.length > 0,
			'Sender Name': chatSenderFullName,
			'Sender Id': chatSender?.id,
			'Chat Room Id': chatInFocus.id,
			'Is Group': `${chatInFocus.isGroupChat}`,
			'Group Name': chatInFocus.name,
		});
		if (files.length === 0) {
			socket.emit('SEND_MESSAGE', { chatId: chatInFocus.id, message });
			setFormValues({ chatMessage: '' });
			return;
		}

		setIsUploading(true);
		try {
			const uploadedAttachments = await createAttachmentUploads({
				files: files.map(({ file }) => ({
					fileName: file.name,
					mimeType: file.type || 'application/octet-stream',
					size: file.size,
				})),
			}).unwrap();
			if (uploadedAttachments.length === 0) {
				toast.error('Unable to attach these files. Please try again.');
				return;
			}
			await uploadAttachmentsToS3(files, uploadedAttachments);
			let nextMessage = message;
			uploadedAttachments.forEach((info) => {
				socket.emit('SEND_MESSAGE', {
					chatId: chatInFocus.id,
					...(nextMessage ? { message: nextMessage } : {}),
					attachment: {
						key: info.key,
						fileName: info.fileName,
						mimeType: info.mimeType,
					},
				});
				nextMessage = '';
			});
			setFormValues({ chatMessage: '' });
			setFiles((currentFiles) => {
				currentFiles.forEach(releaseAttachmentPreview);
				return [];
			});
		} catch (error: any) {
			const errorCode = error?.response?.data?.message ?? error?.data?.message;
			if (errorCode === 'LIMIT_FILE_SIZE') {
				toast.error('Upload size cannot exceed 10 MB');
			} else if (errorCode === 'LIMIT_FILE_COUNT') {
				toast.error('Attachment cannot exceed 4 files');
			} else {
				toast.error('Unable to upload attachments. Please try again.');
			}
		} finally {
			setIsUploading(false);
		}
	};

	const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
		const selectedFiles = Array.from(event.target.files ?? []);
		const eligibleFiles = selectedFiles.filter(isAllowedChatAttachment);
		if (selectedFiles.some((file) => file.size > MAX_CHAT_ATTACHMENT_SIZE)) {
			toast.error('Each attachment must be 10 MB or smaller');
		}
		if (eligibleFiles.length !== selectedFiles.length && selectedFiles.every((file) => file.size <= MAX_CHAT_ATTACHMENT_SIZE)) {
			toast.error('This file type is not supported');
		}

		setFiles((currentFiles) => {
			const uniqueFiles = eligibleFiles.filter(
				(file) =>
					!currentFiles.some(
						(attachment) =>
							attachment.file.name === file.name &&
							attachment.file.size === file.size &&
							attachment.file.lastModified === file.lastModified
					)
			);
			const remainingSlots = MAX_CHAT_ATTACHMENTS - currentFiles.length;
			const attachments = uniqueFiles.slice(0, Math.max(remainingSlots, 0)).map((file) => ({
				id: `${file.name}-${file.lastModified}-${file.size}`,
				file,
				previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
			}));
			if (attachments.length < uniqueFiles.length || uniqueFiles.length < eligibleFiles.length) {
				toast.error('You can attach up to 4 files');
			}
			return [...currentFiles, ...attachments];
		});
		event.target.value = '';
	};

	return (
		<>
			<form
				className="flex min-w-0 flex-1"
				onSubmit={(event) => {
					event.preventDefault();
					handleSubmit();
				}}
			>
					<ChatRichTextEditor
						value={formValues.chatMessage ?? ''}
						onChange={(chatMessage) => formValue({ chatMessage })}
						onFocus={updateSeenMessages}
						onSubmit={handleSubmit}
					/>
				</form>
				<label className="relative ml-1 inline-flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-full text-slate-600 transition-colors hover:bg-slate-100 hover:text-primary focus-within:ring-2 focus-within:ring-ring">
					<span className="sr-only">Attach files</span>
					<input accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip" className="sr-only" disabled={isUploading} multiple type="file" onChange={handleFileUpload} />
					<span className="inline-flex items-center justify-center">
						<Paperclip className="size-5" />
					</span>
					{files.length > 0 && <span className="absolute right-0 top-0 inline-flex size-4 items-center justify-center rounded-full bg-primary text-[10px] font-semibold text-primary-foreground">{files.length}</span>}
				</label>
				<Button aria-label="Send message" className="ml-1 shrink-0 rounded-full" disabled={isUploading || (!formValues.chatMessage?.trim() && files.length === 0)} size="icon" onClick={handleSubmit} type="button">
					<Send className="send-message-icon-message-room" />
				</Button>
		</>
	);
}
